import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import logger from '../../utils/logging/logger';
import { SORT_OPTIONS, sortCourses, isPreviousCourse, readSavedCart, saveCart, getBundleOffer } from './courseCatalogHelpers';

export default function useCourseCatalog() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();

  // Check if user came from login/signup with redirect=/catalog
  const searchParams = new URLSearchParams(location.search);
  const redirectParam = searchParams.get('redirect');
  const redirectFromStorage = typeof window !== 'undefined'
    ? localStorage.getItem('faithinsight_login_redirect')
    : null;

  // Show message if redirect parameter is /catalog in URL or was stored in localStorage
  const showRedirectMessage = redirectParam === '/catalog' || redirectFromStorage === '/catalog';

  // Clear the redirect from localStorage and URL after showing the message
  useEffect(() => {
    if (showRedirectMessage) {
      // Clear from localStorage
      if (redirectFromStorage === '/catalog') {
        localStorage.removeItem('faithinsight_login_redirect');
      }

      // Clear from URL query parameters (replace state to remove ?redirect=/catalog from URL)
      if (redirectParam === '/catalog') {
        const newSearchParams = new URLSearchParams(location.search);
        newSearchParams.delete('redirect');
        const newSearch = newSearchParams.toString();
        const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;
        window.history.replaceState({}, '', newUrl);
      }
    }
  }, [showRedirectMessage, redirectFromStorage, redirectParam, location.search, location.pathname]);
  const [courses, setCourses] = useState([]);
  const [additionalProducts, setAdditionalProducts] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState(SORT_OPTIONS.newest);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [userEnrollments, setUserEnrollments] = useState([]);
  const [bulkPricing, setBulkPricing] = useState(null);
  const [bulkRules, setBulkRules] = useState([]);
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  // The saved cart is restored once, after the catalog first loads (only then do we know
  // which saved items still exist); until then nothing is written back, so a failed load
  // can't wipe it.
  const cartRestoredRef = useRef(false);
  const [cartReady, setCartReady] = useState(false);
  const [checkoutNotice, setCheckoutNotice] = useState('');

  const activeCourses = filteredCourses.filter(course => !isPreviousCourse(course));
  const previousCourses = filteredCourses.filter(isPreviousCourse);
  // Unaffected by the search box: the hero (season, bundle offer) describes the whole catalog.
  const allActiveCourses = courses.filter(course => !isPreviousCourse(course));

  // Fetch user enrollments
  const fetchUserEnrollments = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const response = await api.getUserEnrollments();
      setUserEnrollments(response.enrollments || []);
    } catch (error) {
      logger.error('Error fetching user enrollments', { error });
      // Don't set error state for enrollments, just log it
    }
  }, [isAuthenticated]);

  // Fetch courses and additional products
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch courses (now includes pricing information). Logged-out visitors
        // get the public catalog and public product list.
        const coursesResponse = isAuthenticated
          ? await api.getPublicCourses()
          : await api.getGuestCatalog();
        const coursesData = coursesResponse.courses || [];

        // Ensure all courses have pricing fields with defaults
        const coursesWithPricing = coursesData.map((course) => ({
          ...course,
          price: course.price ?? 0,
          currency: course.currency || 'USD',
          isPaidCourse: course.isPaidCourse ?? false
        }));

        // Fetch additional products
        const productsResponse = isAuthenticated
          ? await api.getActiveAdditionalProducts()
          : await api.getGuestProducts();
        const productsData = productsResponse.products || [];

        // Only feeds the bundle-offer card, so a failure here must not break the catalog.
        try {
          const rulesResponse = await api.getPublicBulkRules();
          setBulkRules(rulesResponse?.bulkPricingRules || []);
        } catch (rulesError) {
          logger.warn('Could not load bundle pricing rules', { error: rulesError });
          setBulkRules([]);
        }

        setCourses(coursesWithPricing);
        setAdditionalProducts(productsData);
        setFilteredCourses(sortCourses(coursesWithPricing, SORT_OPTIONS.newest));
        setFilteredProducts(productsData);

        if (!cartRestoredRef.current) {
          cartRestoredRef.current = true;
          const saved = readSavedCart();
          setSelectedCourses(coursesWithPricing.filter(
            (course) => saved.courses.includes(String(course.code)) && !course.isEnrolled
          ));
          setSelectedProducts(productsData.filter((product) => saved.products.includes(String(product.id))));
          setCartReady(true);
        }

        // Fetch user enrollments if authenticated
        if (isAuthenticated) {
          await fetchUserEnrollments();
        }
      } catch (error) {
        logger.error('Error fetching data', { error });
        setError('Failed to load courses and products');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isAuthenticated, user, fetchUserEnrollments]);

  // Keep the saved cart in step with the selection (and drop a stale checkout notice).
  useEffect(() => {
    if (cartReady) saveCart(selectedCourses, selectedProducts);
    setCheckoutNotice('');
  }, [cartReady, selectedCourses, selectedProducts]);

  // Filter courses and products based on search term and price filter
  useEffect(() => {
    let filteredCourses = courses;
    let filteredProducts = additionalProducts;

    // Filter by search term
    if (searchTerm) {
      filteredCourses = filteredCourses.filter(course =>
        course.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        course.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        course.instructor?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        course.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );

      filteredProducts = filteredProducts.filter(product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }


    setFilteredCourses(sortCourses(filteredCourses, sortBy));
    setFilteredProducts(filteredProducts);
  }, [courses, additionalProducts, searchTerm, sortBy]);


  const handleMultiEnroll = async () => {
    // Pre-fetch CSRF token before navigating to prevent "CSRF token is invalid or expired" errors
    // (signed-in only: guest checkout is CSRF-exempt and guests can't get a token)
    if (isAuthenticated) {
      try {
        await api.fetchCSRFToken();
      } catch (error) {
        logger.warn('Failed to pre-fetch CSRF token before checkout', { error });
        // Continue with navigation anyway - PaymentForm will try to fetch it again
      }
    }

    // If only courses are selected, go directly to payment form
    if (selectedCourses.length > 0 && selectedProducts.length === 0) {
      const courseCodes = selectedCourses.map(course => course.code);
      // Logged-out visitors start at the email-first step (existing students
      // sign in inline; new ones check out as guests and get an account after paying).
      const target = isAuthenticated ? '/enroll/multiple' : '/enroll/flow';
      navigate(`${target}/${courseCodes.join(',')}`, { state: { scrollToTop: true } });
      return;
    }

    // If only products are selected, go directly to payment form
    if (selectedProducts.length > 0 && selectedCourses.length === 0) {
      const productIds = selectedProducts.map(product => product.id);
      // Logged-out visitors start at the same email-first step as for courses.
      const target = isAuthenticated ? '/purchase/products' : '/enroll/products';
      navigate(`${target}/${productIds.join(',')}`, { state: { scrollToTop: true } });
      return;
    }

    // Courses and products are separate purchases (there is no combined payment page).
    if (selectedCourses.length > 0 && selectedProducts.length > 0) {
      setCheckoutNotice('Courses and products are purchased separately. Please check out one first, then the other.');
      return;
    }
  };

  const toggleCourseSelection = (course) => {
    setSelectedCourses(prev => {
      const isSelected = prev.some(selected => selected.id === course.id);
      if (isSelected) {
        return prev.filter(selected => selected.id !== course.id);
      } else {
        return [...prev, course];
      }
    });
  };

  // Puts every course of the bundle offer in the cart (ones already there stay).
  const addBundleToCart = (offer) => {
    setSelectedCourses(prev => [
      ...prev,
      ...offer.courses.filter(course => !prev.some(selected => selected.id === course.id))
    ]);
  };

  // Takes the bundle's courses back out of the cart (anything else in the cart stays).
  const removeBundleFromCart = (offer) => {
    const bundleIds = new Set(offer.courses.map(course => course.id));
    setSelectedCourses(prev => prev.filter(selected => !bundleIds.has(selected.id)));
  };

  const toggleProductSelection = (product) => {
    setSelectedProducts(prev => {
      const isSelected = prev.some(selected => selected.id === product.id);
      if (isSelected) {
        return prev.filter(selected => selected.id !== product.id);
      } else {
        return [...prev, product];
      }
    });
  };

  // Multi-course discount for the sticky bar. The server decides it (signed-in: the bulk-pricing
  // calculator; logged-out: the guest quote), so the bar shows what checkout will charge.
  useEffect(() => {
    let cancelled = false;
    const calculateBulkPricing = async () => {
      const paidCourses = selectedCourses.filter(course => course.isPaidCourse);
      if (paidCourses.length < 2) {
        setBulkPricing(null);
        return;
      }
      try {
        if (isAuthenticated) {
          const response = await api.calculateBulkPricing(paidCourses.map(course => parseInt(course.id)));
          if (!cancelled) setBulkPricing(response.success && response.bulkPricing ? response : null);
        } else {
          const response = await api.guestQuote(paidCourses.map(course => course.code), null);
          if (!cancelled) setBulkPricing(response.success && response.bulkPricing ? response.bulkPricing : null);
        }
      } catch (error) {
        logger.error('Error calculating bulk pricing', { error });
        if (!cancelled) setBulkPricing(null);
      }
    };

    calculateBulkPricing();
    return () => { cancelled = true; };
  }, [selectedCourses, isAuthenticated]);

  // Check if user is enrolled in a course
  const isEnrolledInCourse = (courseId) => {
    return courses.some(course => course.id === courseId && course.isEnrolled) ||
      userEnrollments.some(enrollment => enrollment.course_id === courseId);
  };

  const bundleOffer = getBundleOffer(allActiveCourses, bulkRules, isEnrolledInCourse);


  const getTotalPrice = () => {
    const courseTotal = selectedCourses.reduce((total, course) => {
      return total + (course.isPaidCourse ? parseFloat(course.price) : 0);
    }, 0);

    const productTotal = selectedProducts.reduce((total, product) => {
      return total + parseFloat(product.price || 0);
    }, 0);

    const subtotal = courseTotal + productTotal;

    // Apply bulk pricing discount if available
    if (bulkPricing && bulkPricing.pricing) {
      // Only apply bulk pricing discount to courses, not products
      const courseDiscount = bulkPricing.pricing.discountAmount || 0;
      return Math.max(0, subtotal - courseDiscount);
    }

    return subtotal;
  };

  const getOriginalTotal = () => {
    const courseTotal = selectedCourses.reduce((total, course) => {
      return total + (course.isPaidCourse ? parseFloat(course.price) : 0);
    }, 0);

    const productTotal = selectedProducts.reduce((total, product) => {
      return total + parseFloat(product.price || 0);
    }, 0);

    return courseTotal + productTotal;
  };

  const toggleCourseDescription = (courseId) => {
    setExpandedDescriptions(prev => ({
      ...prev,
      [`course-${courseId}`]: !prev[`course-${courseId}`]
    }));
  };

  return {
    isAuthenticated,
    checkoutNotice,
    showRedirectMessage,
    loading,
    error,
    searchTerm, setSearchTerm,
    sortBy, setSortBy,
    filteredCourses,
    activeCourses,
    previousCourses,
    filteredProducts,
    selectedCourses,
    selectedProducts,
    expandedDescriptions,
    bulkPricing,
    allActiveCourses,
    bundleOffer,
    addBundleToCart,
    removeBundleFromCart,
    isEnrolledInCourse,
    toggleCourseSelection,
    toggleProductSelection,
    toggleCourseDescription,
    getTotalPrice,
    getOriginalTotal,
    handleMultiEnroll,
  };
}
